import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, Eye, EyeOff } from 'react-feather';
import { useAuth } from '../context/AuthContext';
import BrandWordmark from '../components/BrandWordmark';
import './Auth.css';

export default function Register() {
  const [form, setForm] = useState({ username: '', email: '', password: '', confirm: '', acceptedPolicies: false });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = (searchParams.get('ref') || '').trim().toUpperCase();

  const validate = () => {
    const errors = {};
    if (!form.username.match(/^[a-zA-Z0-9_]{3,50}$/)) {
      errors.username = 'Must be 3-50 characters: letters, numbers, underscores only';
    }
    if (!form.email.includes('@') || !form.email.includes('.')) {
      errors.email = 'Enter a valid email address';
    }
    if (form.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    if (form.password !== form.confirm) {
      errors.confirm = 'Passwords do not match';
    }
    if (!form.acceptedPolicies) {
      errors.acceptedPolicies = 'You must accept the Privacy Policy, Terms of Service, and Refund Policy';
    }
    return errors;
  };

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((current) => ({ ...current, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    setServerError('');
    setLoading(true);
    try {
      await register({
        username: form.username,
        email: form.email,
        password: form.password,
        referral_code: referralCode || undefined,
      });
      navigate('/dashboard');
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      setServerError(
        apiErrors ? apiErrors[0].msg : (err.response?.data?.error || err.message || 'Registration failed. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Prevent registration if policies are not accepted
    if (!form.acceptedPolicies) {
      setFieldErrors({ acceptedPolicies: 'You must accept the Privacy Policy, Terms of Service, and Refund Policy before registering.' });
      return;
    }
    
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '590278303235-ekemmg4m5fesou31hb9r7h954v27678a.apps.googleusercontent.com';
    const redirectUri = window.location.origin + window.location.pathname;
    
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` + 
      `client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=id_token` +
      `&scope=${encodeURIComponent('openid email profile')}` +
      `&nonce=nicebooster_nonce` +
      (referralCode ? `&state=${encodeURIComponent(referralCode)}` : '');
      
    window.location.href = googleAuthUrl;
  };

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('id_token=')) {
      const params = new URLSearchParams(hash.replace('#', '?'));
      const idToken = params.get('id_token');
      const refCodeFromState = params.get('state');
      
      if (idToken) {
        window.history.replaceState(null, null, window.location.pathname);
        
        const loginWithToken = async () => {
          setServerError('');
          setLoading(true);
          try {
            await loginWithGoogle(idToken, refCodeFromState || null);
            navigate('/dashboard');
          } catch (err) {
            setServerError(err.response?.data?.error || 'Google registration failed. Please try again.');
          } finally {
            setLoading(false);
          }
        };
        loginWithToken();
      }
    }
  }, [loginWithGoogle, navigate]);

  const getStrength = (password) => {
    if (!password) return 0;
    if (password.length < 8) return 1;
    if (password.match(/[A-Z]/) && password.match(/[0-9]/) && password.length >= 12) return 3;
    if (password.length >= 8) return 2;
    return 1;
  };

  const strength = getStrength(form.password);
  const strengthMeta = [
    null,
    { label: 'Weak', color: 'var(--red-500)' },
    { label: 'Fair', color: 'var(--yellow-500)' },
    { label: 'Strong', color: 'var(--green-500)' },
  ];

  return (
    <div className="auth-page">
      <div className="auth-bg-grid" />
      <div className="auth-card">
        <div className="auth-logo">
          <BrandWordmark className="auth-brand-mark" />
        </div>
        <h1 className="auth-title">Create account</h1>
        <p className="auth-sub">Join thousands growing their social presence</p>

        {serverError && <div className="alert alert-error">{serverError}</div>}
        {referralCode && (
          <div className="alert alert-success" style={{ marginBottom: 16 }}>
            Referral offer active. This account will get 10% off the first 5 orders after signup.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Username</label>
            <input
              className="form-input"
              style={fieldErrors.username ? { borderColor: 'var(--red-500)' } : {}}
              type="text"
              value={form.username}
              placeholder="e.g. Harry_Potter"
              autoComplete="username"
              onChange={(e) => handleChange('username', e.target.value)}
              required
            />
            {fieldErrors.username && <div style={{ fontSize: 12, color: 'var(--red-500)', marginTop: 4 }}><AlertCircle size={12} style={{ display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} /> {fieldErrors.username}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              className="form-input"
              style={fieldErrors.email ? { borderColor: 'var(--red-500)' } : {}}
              type="email"
              value={form.email}
              placeholder="you@example.com"
              autoComplete="email"
              onChange={(e) => handleChange('email', e.target.value)}
              required
            />
            {fieldErrors.email && <div style={{ fontSize: 12, color: 'var(--red-500)', marginTop: 4 }}><AlertCircle size={12} style={{ display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} /> {fieldErrors.email}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div className="auth-password-field">
              <input
                className="form-input"
                style={fieldErrors.password ? { borderColor: 'var(--red-500)' } : {}}
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                placeholder="min 8 characters"
                autoComplete="new-password"
                onChange={(e) => handleChange('password', e.target.value)}
                required
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {form.password.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      style={{
                        height: 3,
                        flex: 1,
                        borderRadius: 3,
                        background: i <= strength ? strengthMeta[strength].color : 'var(--border)',
                        transition: 'background 0.3s',
                      }}
                    />
                  ))}
                </div>
                <span style={{ fontSize: 11, color: strengthMeta[strength].color, fontWeight: 600, minWidth: 40 }}>
                  {strengthMeta[strength].label}
                </span>
              </div>
            )}
            {fieldErrors.password && <div style={{ fontSize: 12, color: 'var(--red-500)', marginTop: 4 }}><AlertCircle size={12} style={{ display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} /> {fieldErrors.password}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <div className="auth-password-field">
              <input
                className="form-input"
                style={
                  fieldErrors.confirm ? { borderColor: 'var(--red-500)' }
                    : form.confirm && form.confirm === form.password ? { borderColor: 'var(--green-500)' }
                      : {}
                }
                type={showConfirmPassword ? 'text' : 'password'}
                value={form.confirm}
                placeholder="repeat your password"
                autoComplete="new-password"
                onChange={(e) => handleChange('confirm', e.target.value)}
                required
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowConfirmPassword((value) => !value)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {form.confirm && form.confirm === form.password && (
              <div style={{ fontSize: 12, color: 'var(--green-500)', marginTop: 4 }}><Check size={12} style={{ display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} /> Passwords match</div>
            )}
            {fieldErrors.confirm && <div style={{ fontSize: 12, color: 'var(--red-500)', marginTop: 4 }}><AlertCircle size={12} style={{ display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} /> {fieldErrors.confirm}</div>}
          </div>

          <div className="auth-consent">
            <label className="auth-consent-label">
              <input
                type="checkbox"
                checked={form.acceptedPolicies}
                onChange={(e) => handleChange('acceptedPolicies', e.target.checked)}
                required
              />
              <span>
                I agree to the{' '}
                <Link to="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</Link>,{' '}
                <Link to="/terms-of-service" target="_blank" rel="noreferrer">Terms of Service</Link>, and{' '}
                <Link to="/refund-policy" target="_blank" rel="noreferrer">Refund Policy</Link>.
              </span>
            </label>
            {fieldErrors.acceptedPolicies && (
              <div className="auth-consent-error">
                <AlertCircle size={12} style={{ display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} /> {fieldErrors.acceptedPolicies}
              </div>
            )}
          </div>

          <button className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: 8 }} disabled={loading}>
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                Creating account...
              </span>
            ) : '→ Create Account'}
          </button>
        </form>

        <p className="auth-terms">Please review and accept our policies before creating your account.</p>

        <div className="auth-divider"><span>Already have an account?</span></div>
        <Link to="/login" className="btn btn-secondary btn-lg" style={{ width: '100%', justifyContent: 'center', textDecoration: 'none' }}>
          Sign In
        </Link>

        <div style={{ margin: '20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, height: 1, backgroundColor: 'var(--border)' }} />
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>or</span>
          <div style={{ flex: 1, height: 1, backgroundColor: 'var(--border)' }} />
        </div>

        <button 
          type="button" 
          className="btn-google" 
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          {loading ? (
            <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
          ) : (
            <>
              <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>
      </div>
    </div>
  );
}
